// @ts-nocheck
/**
 * Division Advanced settings — per-division overrides of the event settings.
 *
 * Every field is nullable in event_division_format_overrides and an empty box
 * means INHERIT. The event's own value is shown as the greyed placeholder, so
 * an empty box reads as "inherit 50 minutes" rather than "zero". Nothing is
 * ever copied from the event into the row: a copied value would silently stop
 * tracking the event setting, which is the whole bug this panel prevents.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Check, Loader2, RotateCcw } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  DIVISION_PALETTE, contrastRatio, isValidHex, resolveDivisionColors, swatchFor,
} from '@/lib/divisionPalette';
import { useThemeMode } from '@/hooks/useDivisionColors';
import PreventedMatchups from './PreventedMatchups';
import DivisionPlaces from './DivisionPlaces';


type Field = { key: string; label: string; unit?: string; type?: 'number' | 'text' };

const FIELDS: Field[] = [
  { key: 'games_per_team_target', label: 'Game guarantee' },
  { key: 'pool_size_target', label: 'Pool size target' },
  { key: 'game_duration_minutes', label: 'Game length', unit: 'min' },
  { key: 'buffer_minutes_between_games', label: 'Time between games', unit: 'min' },
  { key: 'min_rest_minutes', label: 'Minimum rest', unit: 'min' },
  { key: 'max_games_per_day', label: 'Max games per day' },
  { key: 'back_to_back_minutes', label: 'Back-to-back threshold', unit: 'min' },
  { key: 'max_gap_minutes', label: 'Maximum gap', unit: 'min' },
  { key: 'bracket_format', label: 'Bracket format', type: 'text' },
  { key: 'period_type', label: 'Period type', type: 'text' },
  { key: 'period_count', label: 'Number of periods' },
  { key: 'max_athletes_per_roster', label: 'Max athletes per roster' },
  { key: 'min_age', label: 'Minimum age' },
  { key: 'max_age', label: 'Maximum age' },
  { key: 'max_point_differential', label: 'Max point differential' },
  { key: 'points_for_win', label: 'Points for a win' },
  { key: 'points_for_tie', label: 'Points for a tie' },
  { key: 'points_for_loss', label: 'Points for a loss' },
  { key: 'classification', label: 'Classification', type: 'text' },
  { key: 'external_id', label: 'External id', type: 'text' },
];

const EVENT_FIELDS = new Set([
  'games_per_team_target', 'pool_size_target', 'game_duration_minutes',
  'buffer_minutes_between_games', 'min_rest_minutes', 'max_games_per_day',
  'back_to_back_minutes', 'max_gap_minutes', 'bracket_format',
  'period_type', 'period_count',
]);

interface Props {
  eventId: string;
  division: string;
  /** Called after a save so pool plans and the grid re-read the numbers. */
  onSaved?: () => void;
}

const DivisionAdvancedSettings: React.FC<Props> = ({ eventId, division, onSaved }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eventCfg, setEventCfg] = useState<any>({});
  const [values, setValues] = useState<Record<string, string>>({});
  /** null = auto-assign (nothing stored). */
  const [color, setColor] = useState<string | null>(null);
  const [hexDraft, setHexDraft] = useState('');
  const mode = useThemeMode();
  /** Active courts for the event, and the venue names to group them by. */
  const [courts, setCourts] = useState<any[]>([]);
  const [venueNames, setVenueNames] = useState<Record<string, string>>({});
  /** Court ids this division is limited to. Empty = every active court. */
  const [allowed, setAllowed] = useState<string[]>([]);
  /** Resolved availability windows, used only for the capacity line. */
  const [windows, setWindows] = useState<any[] | null>(null);
  /** Games already placed, per court, so removing a court can be honest. */
  const [gamesByCourt, setGamesByCourt] = useState<Record<string, number>>({});
  const [savingCourts, setSavingCourts] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [cfg, ov] = await Promise.all([
      (supabase as any).from('event_format_config').select('*').eq('event_id', eventId).maybeSingle(),
      (supabase as any).from('event_division_format_overrides')
        .select('*').eq('event_id', eventId).eq('division', division).maybeSingle(),
    ]);
    setEventCfg(cfg?.data ?? {});
    const row = ov?.data ?? {};
    const next: Record<string, string> = {};
    for (const f of FIELDS) next[f.key] = row[f.key] == null ? '' : String(row[f.key]);
    setValues(next);
    setColor(row.schedule_color ?? null);
    setHexDraft(typeof row.schedule_color === 'string' && row.schedule_color.startsWith('#') ? row.schedule_color : '');

    // ---- courts -------------------------------------------------------
    const [courtRes, allowRes, availRes, gameRes] = await Promise.all([
      (supabase as any).from('event_courts')
        .select('id, name, venue_id, is_active, sort_order').eq('event_id', eventId).order('sort_order'),
      (supabase as any).from('event_division_courts')
        .select('court_id').eq('event_id', eventId).eq('division', division),
      (supabase as any).rpc('rle_resolved_availability', { p_event_id: eventId }),
      // scheduled_games is keyed by schedule version, not by event.
      (supabase as any).from('scheduled_games')
        .select('court_id, schedule_versions!inner(event_id)')
        .eq('schedule_versions.event_id', eventId)
        .eq('division', division)
        .neq('game_status', 'cancelled')
        .not('court_id', 'is', null),
    ]);
    const activeCourts = (courtRes?.data ?? []).filter((c: any) => c.is_active !== false);
    setCourts(activeCourts);
    setAllowed((allowRes?.data ?? []).map((r: any) => r.court_id));
    setWindows(availRes?.error ? null : (availRes?.data ?? []));
    const counts: Record<string, number> = {};
    for (const g of gameRes?.data ?? []) counts[g.court_id] = (counts[g.court_id] ?? 0) + 1;
    setGamesByCourt(counts);
    const venueIds = [...new Set(activeCourts.map((c: any) => c.venue_id).filter(Boolean))];
    if (venueIds.length) {
      const { data: vs } = await (supabase as any).from('venues').select('id, name').in('id', venueIds);
      setVenueNames(Object.fromEntries((vs ?? []).map((v: any) => [v.id, v.name])));
    } else setVenueNames({});

    setLoading(false);
  }, [eventId, division]);

  /**
   * Empty means ALL. A division with nothing ticked may use every active
   * court — that is the same rule the placer and the grid use, written once on
   * the server in _shared/division-settings.ts and mirrored in this one place.
   */
  const effectiveCourts = allowed.length ? allowed : courts.map((c: any) => c.id);

  /** The division's own slot length: its override, else the event's. */
  const divSlotMinutes = useMemo(() => {
    const gl = Number(values.game_duration_minutes || eventCfg?.game_duration_minutes || 0);
    const buf = Number(values.buffer_minutes_between_games || eventCfg?.buffer_minutes_between_games || 0);
    return gl > 0 ? gl + (buf || 0) : 60;
  }, [values.game_duration_minutes, values.buffer_minutes_between_games, eventCfg]);

  /** Slots on the courts this division may use — not the event's total. */
  const divisionSlots = useMemo(() => {
    if (!windows) return null;
    const allowedSet = new Set(effectiveCourts);
    const mins = (t: any) => {
      const [h, m] = String(t ?? '').split(':').map(Number);
      return Number.isFinite(h) ? h * 60 + (m || 0) : null;
    };
    let n = 0;
    for (const w of windows) {
      if (!allowedSet.has(w.court_id)) continue;
      const a = mins(w.start_time), b = mins(w.end_time);
      if (a == null || b == null || b <= a) continue;
      n += Math.max(0, Math.floor((b - a) / divSlotMinutes));
    }
    return n;
  }, [windows, effectiveCourts.join('|'), divSlotMinutes]);

  /**
   * Saving the list is a diff, never a wipe-and-rewrite: an interrupted save
   * must not leave a division with a half-written allowlist.
   */
  const saveCourts = async (next: string[]) => {
    setSavingCourts(true);
    const before = new Set(allowed);
    const after = new Set(next);
    const toAdd = next.filter((id) => !before.has(id));
    const toRemove = allowed.filter((id) => !after.has(id));
    let error: any = null;
    if (toAdd.length) {
      const res = await (supabase as any).from('event_division_courts')
        .upsert(toAdd.map((court_id) => ({ event_id: eventId, division, court_id })),
          { onConflict: 'event_id,division,court_id' });
      error = res.error;
    }
    if (!error && toRemove.length) {
      const res = await (supabase as any).from('event_division_courts')
        .delete().eq('event_id', eventId).eq('division', division).in('court_id', toRemove);
      error = res.error;
    }
    setSavingCourts(false);
    if (error) { toast.error(error.message); return; }
    setAllowed(next);
    toast.success(
      next.length
        ? `${division} is limited to ${next.length} court${next.length === 1 ? '' : 's'}.`
        : `${division} can use all ${courts.length} courts again.`,
    );
    onSaved?.();
  };

  /**
   * Removing a court that already holds this division's games never moves or
   * deletes them — it says how many there are and asks. Left in place, they
   * are raised by the publish check, where the director can see them.
   */
  const toggleCourt = (courtId: string, on: boolean) => {
    const base = allowed.length ? allowed : [];
    let next: string[];
    if (on) {
      next = [...new Set([...base, courtId])];
      // Ticking the last one back on means "all courts" again, not "these
      // eight" — otherwise a court added to the event next week would be
      // silently unavailable to this division.
      if (next.length === courts.length) next = [];
    }
    else {
      // First untick from "all": ticking becomes explicit, so unticking one
      // court means "every other court", not "no courts".
      const start = allowed.length ? allowed : courts.map((c: any) => c.id);
      next = start.filter((id) => id !== courtId);
      const placed = gamesByCourt[courtId] ?? 0;
      if (placed > 0) {
        const name = courts.find((c: any) => c.id === courtId)?.name ?? 'that court';
        const ok = window.confirm(
          `${placed} ${division} game${placed === 1 ? ' is' : 's are'} already on ${name}. ` +
          `Removing the court will not move ${placed === 1 ? 'it' : 'them'} — ${placed === 1 ? 'it' : 'they'} will stay put ` +
          `and be flagged before you publish. Remove the court anyway?`,
        );
        if (!ok) return;
      }
      if (next.length === courts.length) next = [];
    }
    void saveCourts(next);
  };

  useEffect(() => { void load(); }, [load]);

  const overridden = useMemo(
    () => FIELDS.filter((f) => values[f.key] !== '' && values[f.key] != null),
    [values],
  );

  /**
   * Tidy-up only. The row is shared by every per-division setting AND the
   * schedule colour, so it may only be removed when literally every settable
   * column is null — otherwise clearing one field would silently discard the
   * others (a director who resets a colour must not lose their game guarantee).
   */
  const cleanupIfEmpty = async () => {
    const { data: row } = await (supabase as any)
      .from('event_division_format_overrides')
      .select('*').eq('event_id', eventId).eq('division', division).maybeSingle();
    if (!row) return;
    const settable = [...FIELDS.map((f) => f.key), 'schedule_color'];
    const allNull = settable.every((k) => row[k] == null);
    if (!allNull) return;
    await (supabase as any)
      .from('event_division_format_overrides')
      .delete().eq('event_id', eventId).eq('division', division);
  };

  const save = async (next: Record<string, string>) => {
    setSaving(true);
    const payload: Record<string, any> = { event_id: eventId, division, updated_at: new Date().toISOString() };
    for (const f of FIELDS) {
      const raw = (next[f.key] ?? '').trim();
      payload[f.key] = raw === '' ? null : f.type === 'text' ? raw : Number(raw);
    }
    const { error } = await (supabase as any)
      .from('event_division_format_overrides')
      .upsert(payload, { onConflict: 'event_id,division' });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    await cleanupIfEmpty();
    toast.success(`Saved settings for ${division}.`);
    onSaved?.();
  };

  /**
   * Colour writes touch the schedule_color column ONLY. Resetting to auto sets
   * it to null with an update — it never deletes the row, because the same row
   * carries the game guarantee, game length and every other per-division
   * setting.
   */
  const saveColor = async (next: string | null) => {
    setColor(next);
    setSaving(true);
    const patch = { schedule_color: next, updated_at: new Date().toISOString() };
    const { data: existing } = await (supabase as any)
      .from('event_division_format_overrides')
      .select('event_id').eq('event_id', eventId).eq('division', division).maybeSingle();
    const { error } = existing
      ? await (supabase as any).from('event_division_format_overrides')
          .update(patch).eq('event_id', eventId).eq('division', division)
      : next == null
        ? { error: null }
        : await (supabase as any).from('event_division_format_overrides')
            .insert({ event_id: eventId, division, ...patch });
    if (!error) await cleanupIfEmpty();
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(next ? `Colour set for ${division}.` : `${division} is back to its automatic colour.`);
    onSaved?.();
  };

  const resolved = resolveDivisionColors(division, color, mode);
  const autoName = swatchFor(division, null).name;
  const hexRatio = isValidHex(hexDraft)
    ? contrastRatio(resolveDivisionColors(division, hexDraft, mode).text, resolveDivisionColors(division, hexDraft, mode).tint)
    : null;

  const setField = (key: string, v: string) => {
    const next = { ...values, [key]: v };
    setValues(next);
  };

  const resetField = (key: string) => {
    const next = { ...values, [key]: '' };
    setValues(next);
    void save(next);
  };

  const placeholderFor = (f: Field) => {
    if (!EVENT_FIELDS.has(f.key)) return 'Not set on the event';
    const v = eventCfg?.[f.key];
    if (v == null || v === '') return 'Not set on the event';
    return `Inherits ${v}${f.unit ? ` ${f.unit}` : ''}`;
  };

  return (
    <div className="rounded-md border">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium"
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Advanced settings
        {overridden.length > 0 && (
          <Badge variant="secondary" className="text-[10px]">
            {overridden.length} override{overridden.length === 1 ? '' : 's'}
          </Badge>
        )}
        {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </button>

      {/* A director should never have to open the panel to learn a division
          is behaving differently. */}
      {!loading && overridden.length > 0 && (
        <div className="px-3 pb-2 text-xs text-amber-700">
          {division} overrides the event: {overridden
            .map((f) => `${f.label} ${values[f.key]}${f.unit ? ` ${f.unit}` : ''}`)
            .join(', ')}.
        </div>
      )}

      {expanded && !loading && (
        <div className="space-y-3 border-t p-3">
          <p className="text-xs text-muted-foreground">
            These settings override the event settings for this division only.
          </p>

          {/* Schedule colour — a palette swatch or a hex you type. */}
          <div className="space-y-2 rounded-md border p-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs">Schedule colour</Label>
              <span
                className="rounded px-1.5 py-0.5 text-[11px] font-semibold"
                style={{ background: resolved.tint, color: resolved.text, boxShadow: `inset 3px 0 0 ${resolved.stripe}` }}
              >
                {division}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {resolved.isAuto ? `Auto — ${autoName}` : resolved.swatchName}
              </span>
              {!resolved.isAuto && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto h-7 text-xs"
                  onClick={() => { setHexDraft(''); void saveColor(null); }}
                >
                  <RotateCcw className="mr-1 h-3 w-3" /> Reset to auto
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {DIVISION_PALETTE.map((sw) => {
                const t = mode === 'dark' ? sw.dark : sw.light;
                const selected = color ? color === sw.id : sw.id === swatchFor(division, null).id;
                return (
                  <button
                    key={sw.id}
                    type="button"
                    title={`${sw.name}${color ? '' : sw.id === swatchFor(division, null).id ? ' (auto)' : ''}`}
                    aria-label={sw.name}
                    onClick={() => { setHexDraft(''); void saveColor(sw.id); }}
                    className="flex h-7 w-7 items-center justify-center rounded border"
                    style={{ background: t.tint, borderColor: t.stripe, boxShadow: selected ? `0 0 0 2px ${t.stripe}` : undefined }}
                  >
                    {selected && <Check className="h-3.5 w-3.5" style={{ color: t.text }} />}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <Input
                className="h-8 w-32 text-sm"
                placeholder="#RRGGBB"
                value={hexDraft}
                onChange={(e) => setHexDraft(e.target.value)}
              />
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                disabled={!isValidHex(hexDraft)}
                onClick={() => void saveColor(hexDraft.startsWith('#') ? hexDraft : `#${hexDraft}`)}
              >
                Use this colour
              </Button>
              {hexDraft && !isValidHex(hexDraft) && (
                <span className="text-[11px] text-destructive">That is not a valid hex colour.</span>
              )}
              {hexRatio != null && hexRatio < 4.5 && (
                <span className="text-[11px] text-amber-700">
                  Text on this colour only reaches {hexRatio.toFixed(1)}:1 — under the 4.5:1 needed to stay readable.
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Left empty, the colour is picked automatically and nothing is saved. The division name always appears
              next to the colour, including in print.
            </p>
          </div>
          {/* Matchups this division must never produce. */}
          <PreventedMatchups eventId={eventId} division={division} onChanged={onSaved} />
          {/* Standings and final places for this division. */}
          <div className="rounded-md border p-3">
            <DivisionPlaces eventId={eventId} division={division} />
          </div>
          {/* Courts this division may use. */}

          <div className="space-y-2 rounded-md border p-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs">Courts</Label>
              {savingCourts && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
              {allowed.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto h-7 text-xs"
                  onClick={() => void saveCourts([])}
                >
                  <RotateCcw className="mr-1 h-3 w-3" /> Use all courts
                </Button>
              )}
            </div>
            {courts.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">This event has no active courts yet.</p>
            ) : (
              <>
                {Object.entries(
                  courts.reduce((acc: Record<string, any[]>, c: any) => {
                    const k = c.venue_id ? (venueNames[c.venue_id] ?? 'Venue') : 'Main venue';
                    (acc[k] ??= []).push(c);
                    return acc;
                  }, {}),
                ).map(([venue, list]: any) => (
                  <div key={venue} className="space-y-1">
                    <div className="text-[11px] font-medium text-muted-foreground">{venue}</div>
                    <div className="grid gap-1 sm:grid-cols-2 xl:grid-cols-3">
                      {list.map((c: any) => {
                        const on = allowed.length ? allowed.includes(c.id) : true;
                        const placed = gamesByCourt[c.id] ?? 0;
                        return (
                          <label key={c.id} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={on}
                              onCheckedChange={(v) => toggleCourt(c.id, !!v)}
                            />
                            <span>{c.name}</span>
                            {placed > 0 && (
                              <span className="text-[11px] text-muted-foreground">
                                {placed} game{placed === 1 ? '' : 's'}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">
                  {allowed.length === 0
                    ? `No courts selected — this division can use all ${courts.length} courts.`
                    : `${allowed.length} of ${courts.length} courts selected${
                        divisionSlots == null ? '.' : ` — ${divisionSlots} game slots for ${division}.`
                      }`}
                </p>
              </>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1">
                <Label className="text-xs">{f.label}</Label>
                <div className="flex items-center gap-1">
                  <Input
                    className="h-8 text-sm"
                    inputMode={f.type === 'text' ? undefined : 'decimal'}
                    placeholder={placeholderFor(f)}
                    value={values[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    onBlur={() => void save(values)}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 shrink-0"
                    title="Reset to event setting"
                    disabled={!values[f.key]}
                    onClick={() => resetField(f.key)}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {saving && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Saving…
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DivisionAdvancedSettings;
